import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  BookOpen,
  Check,
  CheckCheck,
  CheckCircle,
  Clock,
  Globe,
  HelpCircle,
  Lock,
  MapPin,
  MoreVertical,
  Search,
  Send,
  Smile,
  Sparkles,
  Trash2,
  User,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

import Modal from '@/components/ui/Modal';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import Pagination from '@/components/ui/Pagination';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { ticketService } from '@/services/ticket.service';
import { courseAssignmentService } from '@/services/course-assignment.service';
import { checkForDuplicates as checkCertDuplicates } from '@/services/certification-assignment.service';
import { learningPathAssignmentService } from '@/services/learning-path-assignment.service';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { useAuth } from '@/hooks/useAuth';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import TooltipArrow from '@/components/ui/TooltipArrow';
import { TICKET_REQUESTS_PERMISSION } from '@/constants/constant-permissions';
import {
  SelfTicketItemType,
  SelfTicketRequestStatus,
  SelfTicketRequestResponseDto,
  TicketMessageDto,
  MessageReactionDto,
} from '@/types/ticket.types';

// Chat message interface for UI display
interface ChatMessage {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_role: 'requester' | 'admin' | 'manager' | 'system';
  message: string;
  timestamp: string;
  is_read_by: string[];
  reactions?: MessageReactionDto[];
  is_deleted?: boolean;
}

// Map API message to UI ChatMessage format
const mapApiMessageToChat = (msg: TicketMessageDto, _current_user_id: string): ChatMessage => ({
  id: msg._id,
  sender_id: msg.sender_id,
  sender_name: msg.sender_name,
  sender_role: msg.sender_role === 'REQUESTER' ? 'requester' : msg.sender_role === 'SYSTEM' ? 'system' : 'admin',
  message: msg.message,
  timestamp: msg.sent_at,
  is_read_by: msg.is_read_by || [],
  reactions: msg.reactions,
  is_deleted: msg.is_deleted,
});

// Common reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👎'];

// Form interfaces
interface MessageFormData {
  message: string;
}

interface DenyFormData {
  reason: string;
}

// Type configurations
const typeConfigMap: Record<SelfTicketItemType, { icon: typeof BookOpen; label: string; color: string; bgColor: string }> = {
  [SelfTicketItemType.COURSE]: {
    icon: BookOpen,
    label: 'Course',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  [SelfTicketItemType.CERTIFICATION]: {
    icon: Award,
    label: 'Certification',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  [SelfTicketItemType.LEARNING_PATH]: {
    icon: MapPin,
    label: 'Learning Path',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  [SelfTicketItemType.GENERAL]: {
    icon: HelpCircle,
    label: 'General',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
};

// "Course" for 1, "Courses" otherwise — avoids the "Course(s)" wording in messages
const pluralizeLabel = (label: string, count: number): string => (count === 1 ? label : `${label}s`);

// Status configurations
const statusConfigMap: Record<SelfTicketRequestStatus, { bg: string; text: string; icon: typeof Clock; label: string }> = {
  [SelfTicketRequestStatus.PENDING]: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: Clock,
    label: 'Pending',
  },
  [SelfTicketRequestStatus.APPROVED]: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: CheckCircle,
    label: 'Approved',
  },
  [SelfTicketRequestStatus.DENIED]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: XCircle,
    label: 'Denied',
  },
  [SelfTicketRequestStatus.CLOSED]: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    icon: Lock,
    label: 'Closed',
  },
};

// Format helpers
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Format date with time for timeline (DD MM YYYY, HH:MM AM/PM)
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hour12}:${minutes} ${ampm}`;
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatMessageDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

// Page Skeleton
const PageSkeleton = (): JSX.Element => (
  <div className="-mx-4 sm:-mx-6 -mt-6 -mb-6 h-[calc(100vh-64px)] flex flex-col bg-gray-50">
    <div className="bg-white border-b px-6 py-4">
      <div className="flex items-center gap-4">
        <SkeletonLoader type="custom" className="w-10 h-10 rounded-lg" />
        <div className="space-y-2">
          <SkeletonLoader type="custom" className="h-5 w-48 rounded" />
          <SkeletonLoader type="custom" className="h-4 w-32 rounded" />
        </div>
      </div>
    </div>
    <div className="flex-1 flex">
      <div className="flex-1 p-6">
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
              <SkeletonLoader type="custom" className="h-16 w-64 rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block w-80 xl:w-[380px] border-l bg-white p-4">
        <SkeletonLoader type="custom" className="h-full rounded-xl" />
      </div>
    </div>
  </div>
);

// Message Bubble Component
const MessageBubble = ({
  message,
  isOwn,
  showAvatar,
  currentUserId,
  onReact,
  onRemoveReaction,
  onDelete,
  ticketStatus,
  approverIds,
  requesterId,
  senderProfileImage,
}: {
  message: ChatMessage;
  isOwn: boolean;
  showAvatar: boolean;
  currentUserId: string;
  onReact: (messageId: string, emoji: string) => void;
  onRemoveReaction: (messageId: string, emoji: string) => void;
  onDelete: (messageId: string, deleteType: 'for_everyone' | 'for_me') => void;
  ticketStatus: SelfTicketRequestStatus;
  approverIds: string[];
  requesterId: string;
  senderProfileImage?: string;
}): JSX.Element => {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReactionDetails, setShowReactionDetails] = useState(false);
  const [selectedReactionFilter, setSelectedReactionFilter] = useState<string>('all');
  const reactionRef = useRef<HTMLDivElement>(null);

  // Determine read status for tick display
  // For requester's message: recipients are all approvers (excluding sender)
  // For approver's message: recipient is the requester (excluding sender if same person)
  const isRequesterMessage = message.sender_role === 'requester';
  const rawRecipientIds = isRequesterMessage ? approverIds : [requesterId];
  // Filter out the sender from recipients (handles case where admin is also the requester)
  const recipientIds = rawRecipientIds.filter(id => id !== message.sender_id);

  // Check how many recipients have read
  const recipientsWhoRead = recipientIds.filter(id => message.is_read_by.includes(id));
  const isReadBySome = recipientsWhoRead.length > 0;
  const isReadByAll = recipientIds.length > 0 && recipientsWhoRead.length === recipientIds.length;

  // Check if current user is the actual sender (for delete permissions)
  const isSender = message.sender_id === currentUserId;

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionRef.current && !reactionRef.current.contains(event.target as Node)) {
        setShowReactionPicker(false);
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReaction = (emoji: string) => {
    const existingReaction = message.reactions?.find(r => r.emoji === emoji);
    if (existingReaction?.user_ids.includes(currentUserId)) {
      onRemoveReaction(message.id, emoji);
    } else {
      onReact(message.id, emoji);
    }
    setShowReactionPicker(false);
  };

  const handleDeleteForEveryone = () => {
    onDelete(message.id, 'for_everyone');
    setShowDeleteModal(false);
  };

  const handleDeleteForMe = () => {
    onDelete(message.id, 'for_me');
    setShowDeleteModal(false);
  };

  // Calculate total reactions count
  const totalReactions = message.reactions?.reduce((sum, r) => sum + r.count, 0) || 0;

  // Get filtered users for reaction details modal
  const getFilteredReactionUsers = () => {
    if (!message.reactions) return [];
    const users: { name: string; emoji: string; userId: string }[] = [];
    const reactionsToShow = selectedReactionFilter === 'all'
      ? message.reactions
      : message.reactions.filter(r => r.emoji === selectedReactionFilter);

    reactionsToShow.forEach(reaction => {
      reaction.user_ids.forEach((userId, idx) => {
        users.push({
          name: (reaction.user_names || [])[idx] || 'Unknown User',
          emoji: reaction.emoji,
          userId,
        });
      });
    });
    return users;
  };

  return (
    <>
      <div
        className={`flex items-end gap-2 group ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => { if (!showReactionPicker) setShowActions(false); }}
      >
        {showAvatar ? (
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
              isOwn ? 'bg-primary-100' : 'bg-gray-100'
            }`}
          >
            {senderProfileImage ? (
              <img
                src={senderProfileImage}
                alt={message.sender_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className={`w-4 h-4 ${isOwn ? 'text-primary-600' : 'text-gray-600'}`} />
            )}
          </div>
        ) : (
          <div className="w-8 flex-shrink-0" />
        )}

        <div className={`max-w-[70%] relative ${isOwn ? 'items-end' : 'items-start'}`} ref={reactionRef}>
          {showAvatar && (
            <p className={`text-xs font-medium mb-1 ${isOwn ? 'text-right text-primary-600' : 'text-gray-600'}`}>
              {message.sender_name}
            </p>
          )}

          <div className="relative">
            <div
              className={`px-4 py-2.5 rounded-2xl ${
                message.is_deleted
                  ? 'bg-gray-100 text-gray-400 italic'
                  : isOwn
                    ? 'bg-primary-100 text-gray-900 rounded-br-md'
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
              }`}
            >
              <p className="text-sm leading-relaxed">{message.message}</p>
            </div>

            {/* Reactions display */}
            {message.reactions && message.reactions.length > 0 && !message.is_deleted && (
              <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                {message.reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    onClick={() => {
                      setSelectedReactionFilter(reaction.emoji);
                      setShowReactionDetails(true);
                    }}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors cursor-pointer ${
                      reaction.user_ids.includes(currentUserId)
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>{reaction.emoji}</span>
                    <span>{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons - available until ticket is closed */}
            {showActions && !message.is_deleted && ticketStatus !== SelfTicketRequestStatus.CLOSED && (
              <div className={`absolute top-0 ${isOwn ? 'left-0 -translate-x-full pr-2' : 'right-0 translate-x-full pl-2'} flex items-center gap-1`}>
                <button
                  onClick={() => setShowReactionPicker(!showReactionPicker)}
                  className="p-1.5 rounded-full bg-white border shadow-sm hover:bg-gray-50 transition-colors"
                  title="Add reaction"
                >
                  <Smile className="w-4 h-4 text-gray-500" />
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="p-1.5 rounded-full bg-white border shadow-sm hover:bg-red-50 transition-colors"
                  title="Delete message"
                >
                  <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-500" />
                </button>
              </div>
            )}

            {/* Quick reaction picker */}
            {showReactionPicker && (
              <div className={`absolute bottom-full mb-2 ${isOwn ? 'right-0' : 'left-0'} bg-white rounded-full shadow-lg border p-1.5 flex gap-1 z-10`}>
                {REACTION_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-lg"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Timestamp and read status */}
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            <p className="text-xs text-gray-400">{formatTime(message.timestamp)}</p>
            {isOwn && !message.is_deleted && (
              <span className={`${isReadByAll ? 'text-primary-500' : 'text-gray-400'}`}>
                {isReadBySome ? (
                  <CheckCheck className="w-4 h-4" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Reaction Details Modal (WhatsApp style) */}
      {showReactionDetails && message.reactions && message.reactions.length > 0 && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowReactionDetails(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-xs shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">
                {totalReactions} {totalReactions === 1 ? 'reaction' : 'reactions'}
              </h3>
              <button
                onClick={() => setShowReactionDetails(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Emoji filter tabs */}
            <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setSelectedReactionFilter('all')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  selectedReactionFilter === 'all'
                    ? 'bg-primary-100 text-primary-700 border border-primary-300'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Smile className="w-3.5 h-3.5" />
                <span>All {totalReactions}</span>
              </button>
              {message.reactions.map((reaction) => (
                <button
                  key={reaction.emoji}
                  onClick={() => setSelectedReactionFilter(reaction.emoji)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    selectedReactionFilter === reaction.emoji
                      ? 'bg-primary-100 text-primary-700 border border-primary-300'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <span>{reaction.emoji}</span>
                  <span>{reaction.count}</span>
                </button>
              ))}
            </div>

            {/* User list */}
            <div className="overflow-y-auto max-h-[240px] py-1">
              {getFilteredReactionUsers().map((user, idx) => (
                <div
                  key={`${user.userId}-${user.emoji}-${idx}`}
                  onClick={() => {
                    if (user.userId === currentUserId) {
                      onRemoveReaction(message.id, user.emoji);
                      if (totalReactions <= 1) {
                        setShowReactionDetails(false);
                      }
                    }
                  }}
                  className={`flex items-center gap-3 px-4 py-2 ${
                    user.userId === currentUserId ? 'hover:bg-red-50 cursor-pointer' : ''
                  }`}
                >
                  {/* User avatar */}
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-primary-700">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  {/* Name and action hint */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.userId === currentUserId ? 'You' : user.name}
                    </p>
                    {user.userId === currentUserId && (
                      <p className="text-[11px] text-gray-400">Click to remove</p>
                    )}
                  </div>
                  {/* Emoji */}
                  <span className="text-lg flex-shrink-0">{user.emoji}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Message Modal (WhatsApp style) */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete message?</h3>
              <p className="text-sm text-gray-600">
                {isSender
                  ? 'Choose how you want to delete this message.'
                  : 'This message will be deleted for you only.'}
              </p>
            </div>
            <div className="border-t">
              {isSender && (
                <button
                  onClick={handleDeleteForEveryone}
                  className="w-full px-6 py-3.5 text-left text-sm border-b text-red-600 hover:bg-red-50"
                >
                  <span className="font-medium">Delete for everyone</span>
                </button>
              )}
              <button
                onClick={handleDeleteForMe}
                className="w-full px-6 py-3.5 text-left text-sm text-gray-700 hover:bg-gray-50 font-medium"
              >
                Delete for me
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="w-full px-6 py-3.5 text-left text-sm text-gray-500 hover:bg-gray-50 border-t"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Ticket Details Sidebar
const TicketDetailsSidebar = ({
  ticket,
  onApprove,
  onDeny,
  onClose,
  isApproving,
  isDenying,
  isClosing,
  showApproversList,
  setShowApproversList,
  isGeneral = false,
  onRemoveApproverClick,
  selectedItemIds,
  setSelectedItemIds,
}: {
  ticket: SelfTicketRequestResponseDto;
  onApprove: () => void;
  onDeny: () => void;
  onClose: () => void;
  isApproving: boolean;
  isDenying: boolean;
  isClosing: boolean;
  showApproversList: boolean;
  setShowApproversList: (show: boolean) => void;
  isGeneral?: boolean;
  onRemoveApproverClick: (approver: any) => void;
  selectedItemIds: string[];
  setSelectedItemIds: (ids: string[]) => void;
}): JSX.Element => {
  const navigate = useNavigate();
  const typeConfig = typeConfigMap[ticket.item_type] || typeConfigMap[SelfTicketItemType.COURSE];
  const statusConfig = statusConfigMap[ticket.status as SelfTicketRequestStatus] || statusConfigMap[SelfTicketRequestStatus.PENDING];
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Profile Section */}
      <div className="flex-shrink-0 pt-6 pb-4 px-4">
        <div className="flex flex-col items-center">
          {/* Large Profile Image */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 via-brand-indigo-500 to-brand-purple-600 flex items-center justify-center shadow-lg ring-4 ring-white overflow-hidden">
            {ticket.requester_profile_image ? (
              <img
                src={ticket.requester_profile_image}
                alt={ticket.requester_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold text-2xl">
                {ticket.requester_name.split(' ').map((n) => n[0]).join('').toUpperCase()}
              </span>
            )}
          </div>
          <span className="mt-2 font-mono text-sm font-semibold text-primary-600">{ticket.ticket_number}</span>
          <h3 className="mt-1 font-semibold text-gray-900 text-base">{ticket.requester_name}</h3>
          <p className="text-xs text-gray-500 truncate max-w-full">{ticket.requester_email}</p>

          {/* Status & Type Badges */}
          <div className="flex items-center gap-2 mt-3">
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </div>
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${typeConfig.bgColor} ${typeConfig.color}`}>
              <TypeIcon className="w-3 h-3" />
              {ticket.item_type === SelfTicketItemType.GENERAL && ticket.requested_suggestion_type
                ? `General - ${typeConfigMap[ticket.requested_suggestion_type]?.label || ticket.requested_suggestion_type}`
                : typeConfig.label}
            </div>
          </div>

          {/* Requested Date */}
          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            Requested on {formatDate(ticket.createdAt)}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-gray-200" />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
        {showApproversList ? (
          /* Approvers List View */
          <div className="space-y-3">
            {/* Header with back button */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-primary-600" />
                Approvers List
              </h3>
              <button
                onClick={() => setShowApproversList(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>

            {/* Approvers */}
            {ticket.approvers && ticket.approvers.length > 0 ? (
              <div className="space-y-2">
                {ticket.approvers.map((approver, index) => {
                  // Get first and last initials only (e.g., "joe selva P" -> "J P")
                  const nameParts = approver.user_name?.trim().split(/\s+/) || [];
                  const initials = nameParts.length === 0 ? '?'
                    : nameParts.length === 1 ? nameParts[0][0]?.toUpperCase() || '?'
                    : (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();

                  return (
                    <div
                      key={approver.user_id || index}
                      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-brand-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0 text-sm overflow-hidden">
                        {approver.profile_image ? (
                          <img
                            src={approver.profile_image}
                            alt={approver.user_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 truncate">{approver.user_name}</p>
                          {approver.role && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 uppercase tracking-wide">
                              {approver.role}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{approver.email}</p>
                      </div>
                       <button
                        onClick={() => {
                          onRemoveApproverClick(approver);
                        }}
                        className="p-1.5 hover:bg-red-50 hover:text-red-600 text-gray-400 rounded-lg transition-colors flex-shrink-0"
                        title="Remove approver"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No approvers assigned</p>
              </div>
            )}
          </div>
        ) : (
          /* Ticket Details View */
          <>
        {/* Items Card - For GENERAL tickets show Description or Suggested Items */}
        {ticket.item_type === SelfTicketItemType.GENERAL ? (
          <>
            {/* Always show Request Description for GENERAL tickets */}
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5 text-brand-purple-600" />
                  Request Description
                </p>
                {/* Generate Button */}
                <button
                  onClick={() => {
                    const suggestionType = ticket.requested_suggestion_type;
                    if (suggestionType === SelfTicketItemType.COURSE) {
                      navigate('/admin/course/add-course', {
                        state: {
                          ticketDescription: ticket.general_description,
                          languageCode: ticket.language_code,
                          courseGenerationConfig: ticket.course_generation_config,
                        }
                      });
                    } else if (suggestionType === SelfTicketItemType.LEARNING_PATH) {
                      navigate('/admin/learning-paths/create', {
                        state: {
                          ticketDescription: ticket.general_description,
                          languageCode: ticket.language_code,
                        }
                      });
                    } else if (suggestionType === SelfTicketItemType.CERTIFICATION) {
                      navigate('/admin/certification/create', {
                        state: {
                          ticketDescription: ticket.general_description,
                          languageCode: ticket.language_code,
                          certificationCourses: ticket.certification_courses,
                          certificationLearningPaths: ticket.certification_learning_paths
                        }
                      });
                    }
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-md hover:from-brand-purple-700 hover:to-brand-indigo-700 transition-all shadow-sm"
                >
                  <Sparkles className="w-3 h-3" />
                  Generate
                </button>
              </div>
              <div className="p-3 rounded-lg bg-brand-purple-50 bg-opacity-50">
                <p className="text-xs text-gray-800 leading-relaxed">
                  {ticket.general_description || 'No description provided'}
                </p>
              </div>
            </div>

            {/* Show Language for GENERAL tickets */}
            {ticket.language_code && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-brand-indigo-600" />
                  Language
                </p>
                <p className="text-sm text-gray-800 font-medium">{ticket.language_code}</p>
              </div>
            )}

            {/* Show Course Generation Config for COURSE suggestion type */}
            {ticket.requested_suggestion_type === SelfTicketItemType.COURSE && ticket.course_generation_config && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-brand-indigo-600" />
                  Course Generation Config
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-brand-indigo-50">
                    <p className="text-[10px] font-semibold text-brand-indigo-600 uppercase tracking-wide mb-0.5">Delivery Type</p>
                    <p className="text-xs text-gray-800 font-medium capitalize">
                      {ticket.course_generation_config.content_delivery_type?.replace(/_/g, ' ') || 'N/A'}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-brand-indigo-50">
                    <p className="text-[10px] font-semibold text-brand-indigo-600 uppercase tracking-wide mb-0.5">Design Theme</p>
                    <p className="text-xs text-gray-800 font-medium capitalize">
                      {ticket.course_generation_config.design_theme || 'N/A'}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-brand-indigo-50">
                    <p className="text-[10px] font-semibold text-brand-indigo-600 uppercase tracking-wide mb-0.5">Modules</p>
                    <p className="text-xs text-gray-800 font-medium">
                      {ticket.course_generation_config.module_generation_config?.min_modules ?? '-'} – {ticket.course_generation_config.module_generation_config?.max_modules ?? '-'}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-brand-indigo-50">
                    <p className="text-[10px] font-semibold text-brand-indigo-600 uppercase tracking-wide mb-0.5">Content Items / Module</p>
                    <p className="text-xs text-gray-800 font-medium">
                      {ticket.course_generation_config.content_generation_config?.min_content_items_per_module ?? '-'} – {ticket.course_generation_config.content_generation_config?.max_content_items_per_module ?? '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show Selected Courses & Learning Paths for CERTIFICATION suggestion type */}
            {ticket.requested_suggestion_type === SelfTicketItemType.CERTIFICATION &&
              ((ticket.certification_courses && ticket.certification_courses.length > 0) ||
               (ticket.certification_learning_paths && ticket.certification_learning_paths.length > 0)) && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5 text-amber-600" />
                  Requested Items for Certification
                </p>

                {/* Selected Courses */}
                {ticket.certification_courses && ticket.certification_courses.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      Courses ({ticket.certification_courses.length})
                    </p>
                    <div className="space-y-1">
                      {ticket.certification_courses.map((course, index) => (
                        <div
                          key={course.item_id}
                          className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50"
                        >
                          <div className="w-4 h-4 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-[9px] font-bold text-indigo-600">{index + 1}</span>
                          </div>
                          <p className="text-xs text-gray-800 truncate font-medium">{course.item_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Learning Paths */}
                {ticket.certification_learning_paths && ticket.certification_learning_paths.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Learning Paths ({ticket.certification_learning_paths.length})
                    </p>
                    <div className="space-y-1">
                      {ticket.certification_learning_paths.map((lp, index) => (
                        <div
                          key={lp.item_id}
                          className="flex items-center gap-2 p-2 rounded-lg bg-green-50"
                        >
                          <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-[9px] font-bold text-green-600">{index + 1}</span>
                          </div>
                          <p className="text-xs text-gray-800 truncate font-medium">{lp.item_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show Suggested Items if ticket has been actioned and has items */}
            {ticket.actioned_by?.action === 'SUGGESTED' && ticket.items && ticket.items.length > 0 && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <TypeIcon className="w-3.5 h-3.5 text-brand-purple-600" />
                    Suggested Items
                  </span>
                  <span className="bg-brand-purple-100 text-brand-purple-600 px-2 py-0.5 rounded-full text-[10px] font-bold">
                    {ticket.items.length}
                  </span>
                </p>
                <div className="space-y-1.5">
                  {ticket.items.map((item, index) => {
                    if (!item) return null;
                    return (
                    <div
                      key={item.item_id || index}
                      className="flex items-center gap-2.5 p-2.5 rounded-lg bg-brand-purple-50 bg-opacity-50"
                    >
                      <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                        <span className="text-[10px] font-bold text-brand-purple-600">{index + 1}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <TooltipArrow content={item.item_name || 'Unknown Item'} position="bottom" delay={200} block>
                          <p className="text-xs text-gray-800 truncate font-medium cursor-default">{item.item_name || 'Unknown Item'}</p>
                        </TooltipArrow>
                      </div>
                    </div>
                  )})}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
            <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <TypeIcon className={`w-3.5 h-3.5 ${typeConfig.color}`} />
                Requested Items
              </span>
              <span className={`${typeConfig.bgColor} ${typeConfig.color} px-2 py-0.5 rounded-full text-[10px] font-bold`}>
                {(ticket.items || []).length}
              </span>
            </p>
            {ticket.status === SelfTicketRequestStatus.PENDING && (
              <p className="text-[11px] text-gray-500 mb-2">
                {selectedItemIds.length === 0
                  ? `Select the ${pluralizeLabel(typeConfig.label, (ticket.items || []).length).toLowerCase()} to approve`
                  : `${selectedItemIds.length} of ${(ticket.items || []).length} selected`}
              </p>
            )}
            <div className="space-y-1.5">
              {(ticket.items || []).map((item, index) => {
                if (!item) return null;
                const isPending = !item.status || item.status === SelfTicketRequestStatus.PENDING;
                return (
                <div
                  key={item.item_id || index}
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg ${typeConfig.bgColor} bg-opacity-50`}
                >
                  {ticket.status === SelfTicketRequestStatus.PENDING && isPending && (
                    <input
                      type="checkbox"
                      checked={selectedItemIds.includes(item.item_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItemIds([...selectedItemIds, item.item_id]);
                        } else {
                          setSelectedItemIds(selectedItemIds.filter(id => id !== item.item_id));
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                    />
                  )}
                  <div className={`w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <span className={`text-[10px] font-bold ${typeConfig.color}`}>{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <TooltipArrow content={item.item_name || 'Unknown Item'} position="bottom" delay={200} block>
                        <p className="text-xs text-gray-800 truncate font-medium cursor-default">{item.item_name || 'Unknown Item'}</p>
                      </TooltipArrow>
                    </div>
                    {!isPending && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        item.status === SelfTicketRequestStatus.APPROVED ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {item.status}
                      </span>
                    )}
                  </div>
                </div>
              )})}
            </div>
          </div>
        )}

        {/* Resolution - Using actioned_by (shown above timeline) */}
        {ticket.actioned_by && (() => {
          const action = ticket.actioned_by.action;
          // Treat SUGGESTED same as APPROVED for display
          const isPositive = action === 'APPROVED' || action === 'SUGGESTED';
          const isDenied = action === 'DENIED';

          const bgClass = isPositive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
          const iconBgClass = isPositive ? 'bg-green-100' : 'bg-red-100';
          const iconColorClass = isPositive ? 'text-green-600' : 'text-red-600';
          const textColorClass = isPositive ? 'text-green-800' : 'text-red-800';
          const subTextColorClass = isPositive ? 'text-green-600' : 'text-red-600';
          const timeColorClass = isPositive ? 'text-green-500' : 'text-red-500';
          const remarksBgClass = isPositive ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100';
          const actionLabel = isPositive ? 'Approved' : 'Denied';
          const remarksLabel = isDenied ? 'Reason:' : 'Remarks:';

          return (
            <div className={`rounded-xl p-3 border shadow-sm ${bgClass}`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconBgClass}`}>
                  {isPositive ? (
                    <CheckCircle className={`w-4 h-4 ${iconColorClass}`} />
                  ) : (
                    <XCircle className={`w-4 h-4 ${iconColorClass}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${textColorClass}`}>
                    {actionLabel}
                  </p>
                  <p className={`text-[10px] truncate ${subTextColorClass}`}>
                    by {ticket.actioned_by.user_name} {ticket.actioned_by.role ? `(${ticket.actioned_by.role})` : ''}
                  </p>
                </div>
              </div>
              {ticket.actioned_by.actioned_at && (
                <p className={`text-[10px] mt-1.5 ${timeColorClass}`}>
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatDate(ticket.actioned_by.actioned_at)} at {formatTime(ticket.actioned_by.actioned_at)}
                </p>
              )}
              {ticket.actioned_by.remarks && (
                <p className={`text-xs mt-2 rounded-lg p-2 ${remarksBgClass}`}>
                  <span className="font-medium">{remarksLabel}</span> {ticket.actioned_by.remarks}
                </p>
              )}
            </div>
          );
        })()}

        {/* Timeline */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            Timeline
          </p>
          <div className="relative">
            {/* Vertical line - green for approved/closed, red for denied, default green */}
            <div className={`absolute left-[7px] top-2 bottom-2 w-0.5 ${
              ticket.status === SelfTicketRequestStatus.DENIED ? 'bg-red-200' : 'bg-green-200'
            }`} />

            <div className="space-y-3">
              {/* Created */}
              <div className="flex items-start gap-3 relative">
                <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm z-10 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Ticket Created</p>
                  <p className="text-xs text-gray-500">{formatDateTime(ticket.createdAt)}</p>
                </div>
              </div>

              {/* Read by users - show each reader */}
              {ticket.read_by && ticket.read_by.length > 0 && ticket.read_by.map((reader, index) => (
                <div key={`reader-${index}`} className="flex items-start gap-3 relative">
                  <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm z-10 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{reader.user_name} read the ticket</p>
                    <p className="text-xs text-gray-500">{formatDateTime(reader.read_at)}</p>
                  </div>
                </div>
              ))}

              {/* Approved/Denied - Highlighted as last stage if not closed */}
              {ticket.actioned_by && (() => {
                const action = ticket.actioned_by.action;
                // Treat SUGGESTED same as APPROVED for display
                const isPositive = action === 'APPROVED' || action === 'SUGGESTED';
                const isClosed = ticket.status === SelfTicketRequestStatus.CLOSED;

                // Determine colors based on action (SUGGESTED shows as Approved in green)
                const dotColor = isPositive ? 'bg-green-500' : 'bg-red-500';
                const ringColor = isPositive ? 'ring-green-200' : 'ring-red-200';
                const textColor = isClosed ? 'text-gray-900' : isPositive ? 'text-green-700' : 'text-red-700';
                const actionLabel = isPositive ? 'Approved' : 'Denied';

                return (
                  <div className="flex items-start gap-3 relative">
                    <div className={`rounded-full border-2 border-white shadow-sm z-10 mt-0.5 ${
                      isClosed
                        ? `w-4 h-4 ${dotColor}`
                        : `w-5 h-5 ${dotColor} ring-2 ${ringColor} -ml-0.5`
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${textColor}`}>
                        {actionLabel}
                      </p>
                      <p className="text-xs text-gray-500">
                        by {ticket.actioned_by.user_name} • {formatDateTime(ticket.actioned_by.actioned_at)}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Closed - Highlighted as last stage */}
              {ticket.status === SelfTicketRequestStatus.CLOSED && ticket.closed_at && (
                <div className="flex items-start gap-3 relative">
                  <div className="w-5 h-5 rounded-full bg-green-500 border-2 border-white shadow-sm z-10 mt-0.5 ring-2 ring-green-200 -ml-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-700">Ticket Closed</p>
                    <p className="text-xs text-gray-500">
                      {ticket.closed_by ? `by ${ticket.closed_by.user_name} • ` : ''}{formatDateTime(ticket.closed_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* Pending indicator - Highlighted as last stage */}
              {ticket.status === SelfTicketRequestStatus.PENDING && (
                <div className="flex items-start gap-3 relative">
                  <div className="w-5 h-5 rounded-full bg-green-400 border-2 border-white shadow-sm z-10 mt-0.5 animate-pulse ring-2 ring-green-200 -ml-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-700">Awaiting Action</p>
                    <p className="text-xs text-gray-500">Pending approval or denial</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Action Buttons - Only show for pending tickets */}
      {ticket.status === SelfTicketRequestStatus.PENDING && (
        <div className="flex-shrink-0 p-4 border-t bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onDeny}
              disabled={isDenying || isApproving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isDenying ? (
                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Deny
            </button>
            <button
              onClick={onApprove}
              disabled={isApproving || isDenying || (!isGeneral && selectedItemIds.length === 0)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium ${
                isGeneral
                  ? 'bg-brand-purple-600 hover:bg-brand-purple-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isApproving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isGeneral ? (
                <HelpCircle className="w-4 h-4" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              {isGeneral ? 'Suggest' : 'Approve'}
            </button>
          </div>
        </div>
      )}

      {/* Close Button - Show for approved or denied tickets (not closed) */}
      {(ticket.status === SelfTicketRequestStatus.APPROVED || ticket.status === SelfTicketRequestStatus.DENIED) && (
        <div className="flex-shrink-0 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={isClosing}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-xl hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isClosing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            Close Ticket
          </button>
        </div>
      )}
    </div>
  );
};

// Deny Modal Component
const DenyModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}): JSX.Element => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<DenyFormData>({
    defaultValues: { reason: '' },
  });

  const onSubmit = (data: DenyFormData): void => {
    onConfirm(data.reason);
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Deny Request" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl border border-red-100">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">You are about to deny this request</p>
            <p className="text-sm text-red-600 mt-1">
              The requester will be notified of your decision. Please provide a reason for denying this request.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Denial <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register('reason', { required: 'Please provide a reason for denial' })}
            rows={4}
            placeholder="Enter the reason for denying this request..."
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
          />
          {errors.reason && (
            <p className="text-sm text-red-500 mt-1">{errors.reason.message}</p>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <XCircle className="w-4 h-4" />
            )}
            Confirm Denial
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Approval settings interface
interface ApprovalSettings {
  is_mandatory: boolean;
  deadline_days?: number;
  send_notification: boolean;
  enable_automatic_reminder: boolean;
  reminder_frequency_days?: number;
  message?: string;
  remarks?: string;
  // For GENERAL tickets (suggest functionality)
  item_type?: 'COURSE' | 'CERTIFICATION' | 'LEARNING_PATH';
  items?: { item_id: string; item_name: string }[];
  // When true, only items in the items array should be assigned (duplicates skipped)
  skip_duplicate_items?: boolean;
  selected_item_ids?: string[];
}

// Suggest Modal for GENERAL tickets
const SuggestModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  ticket,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (settings: ApprovalSettings) => void;
  isLoading: boolean;
  ticket?: SelfTicketRequestResponseDto;
}): JSX.Element => {
  // Use the requested_suggestion_type from ticket, default to COURSE
  const requestedType = (ticket?.requested_suggestion_type as 'COURSE' | 'CERTIFICATION' | 'LEARNING_PATH') || 'COURSE';
  const [selectedItemType, setSelectedItemType] = useState<'COURSE' | 'CERTIFICATION' | 'LEARNING_PATH'>(requestedType);
  const [selectedItems, setSelectedItems] = useState<{ item_id: string; item_name: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Use centralized debounce hook
  const debouncedSearch = useDebounce(searchQuery, DEFAULT_DEBOUNCE_DELAY);

  const [settings, setSettings] = useState<ApprovalSettings>({
    is_mandatory: false,
    deadline_days: undefined,
    send_notification: true,
    enable_automatic_reminder: false,
    reminder_frequency_days: 7,
    message: '',
    remarks: '',
  });

  const [deadlineError, setDeadlineError] = useState('');

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Reset when modal opens or item type changes
  useEffect(() => {
    if (isOpen) {
      // Reset to the requested suggestion type
      setSelectedItemType(requestedType);
      setSelectedItems([]);
      setSearchQuery('');
      setCurrentPage(1);
      setSettings({
        is_mandatory: false,
        deadline_days: undefined,
        send_notification: true,
        enable_automatic_reminder: false,
        reminder_frequency_days: 7,
        message: '',
        remarks: '',
      });
      setDeadlineError('');
    }
  }, [isOpen, requestedType]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems([]);
  }, [selectedItemType]);

  // Query for available items based on selected type
  const coursesQuery = useQuery({
    queryKey: ['suggest-courses', debouncedSearch, currentPage, itemsPerPage, ticket?.requester_id],
    queryFn: () => ticketService.getAvailableCourses({
      user_id: ticket?.requester_id,
      search: debouncedSearch || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: isOpen && selectedItemType === 'COURSE' && !!ticket?.requester_id,
  });

  const certificationsQuery = useQuery({
    queryKey: ['suggest-certifications', debouncedSearch, currentPage, itemsPerPage, ticket?.requester_id],
    queryFn: () => ticketService.getAvailableCertifications({
      user_id: ticket?.requester_id,
      search: debouncedSearch || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: isOpen && selectedItemType === 'CERTIFICATION' && !!ticket?.requester_id,
  });

  const learningPathsQuery = useQuery({
    queryKey: ['suggest-learning-paths', debouncedSearch, currentPage, itemsPerPage, ticket?.requester_id],
    queryFn: () => ticketService.getAvailableLearningPaths({
      user_id: ticket?.requester_id,
      search: debouncedSearch || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: isOpen && selectedItemType === 'LEARNING_PATH' && !!ticket?.requester_id,
  });

  const getCurrentQuery = () => {
    switch (selectedItemType) {
      case 'COURSE': return coursesQuery;
      case 'CERTIFICATION': return certificationsQuery;
      case 'LEARNING_PATH': return learningPathsQuery;
    }
  };

  const currentQuery = getCurrentQuery();
  const items = currentQuery.data?.data || [];
  const pagination = currentQuery.data?.pagination;
  const isLoadingItems = currentQuery.isLoading;

  const toggleItemSelection = (item: { _id: string; title: string }) => {
    setSelectedItems(prev => {
      const exists = prev.some(i => i.item_id === item._id);
      if (exists) {
        return prev.filter(i => i.item_id !== item._id);
      }
      return [...prev, { item_id: item._id, item_name: item.title }];
    });
  };

  const handleDeadlineDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setSettings(prev => ({ ...prev, deadline_days: undefined }));
      setDeadlineError('');
      return;
    }
    const days = parseInt(value);
    setDeadlineError(days <= 0 ? 'Deadline must be at least 1 day' : '');
    setSettings(prev => ({ ...prev, deadline_days: days }));
  };

  const handleConfirm = () => {
    if (deadlineError || selectedItems.length === 0) return;
    // Don't send item_type - backend uses requested_suggestion_type from ticket
    onConfirm({
      ...settings,
      items: selectedItems,
    });
  };

  if (!isOpen) return <></>;

  const typeConfig = {
    COURSE: { icon: BookOpen, label: 'Course', color: 'text-indigo-600', bgColor: 'bg-indigo-100', borderColor: 'border-indigo-500', hoverBg: 'hover:bg-indigo-50' },
    CERTIFICATION: { icon: Award, label: 'Certification', color: 'text-amber-600', bgColor: 'bg-amber-100', borderColor: 'border-amber-500', hoverBg: 'hover:bg-amber-50' },
    LEARNING_PATH: { icon: MapPin, label: 'Learning Path', color: 'text-green-600', bgColor: 'bg-green-100', borderColor: 'border-green-500', hoverBg: 'hover:bg-green-50' },
  };

  const currentTypeConfig = typeConfig[selectedItemType];
  const CurrentTypeIcon = currentTypeConfig.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Suggest Items</h2>
                <p className="text-brand-purple-100 text-sm">For {ticket?.requester_name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">
            {/* Request Description Card */}
            {ticket?.general_description && (
              <div className="bg-gradient-to-r from-brand-purple-50 to-brand-indigo-50 rounded-xl p-4 border border-brand-purple-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-brand-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand-purple-700 uppercase tracking-wide mb-1">Request Description</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{ticket?.general_description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Course Generation Config in modal */}
            {ticket?.requested_suggestion_type === SelfTicketItemType.COURSE && ticket?.course_generation_config && (
              <div className="bg-gradient-to-r from-brand-indigo-50 to-brand-blue-50 rounded-xl p-4 border border-brand-indigo-100">
                <p className="text-xs font-semibold text-brand-indigo-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" />
                  Course Generation Config
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-xs"><span className="text-gray-500">Delivery:</span> <span className="font-medium text-gray-800 capitalize">{ticket.course_generation_config.content_delivery_type?.replace(/_/g, ' ') || 'N/A'}</span></div>
                  <div className="text-xs"><span className="text-gray-500">Theme:</span> <span className="font-medium text-gray-800 capitalize">{ticket.course_generation_config.design_theme || 'N/A'}</span></div>
                  <div className="text-xs"><span className="text-gray-500">Modules:</span> <span className="font-medium text-gray-800">{ticket.course_generation_config.module_generation_config?.min_modules ?? '-'}–{ticket.course_generation_config.module_generation_config?.max_modules ?? '-'}</span></div>
                  <div className="text-xs"><span className="text-gray-500">Items/Module:</span> <span className="font-medium text-gray-800">{ticket.course_generation_config.content_generation_config?.min_content_items_per_module ?? '-'}–{ticket.course_generation_config.content_generation_config?.max_content_items_per_module ?? '-'}</span></div>
                </div>
              </div>
            )}

            {/* Item Type Selection */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-3 block flex items-center gap-2">
                <span>Suggestion Type</span>
                <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Selected by learner</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['COURSE', 'CERTIFICATION', 'LEARNING_PATH'] as const).map((type) => {
                  const config = typeConfig[type];
                  const TypeIcon = config.icon;
                  const isSelected = selectedItemType === type;
                  const isDisabled = type !== requestedType;
                  return (
                    <button
                      key={type}
                      onClick={() => !isDisabled && setSelectedItemType(type)}
                      disabled={isDisabled}
                      className={`relative flex flex-col items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        isDisabled
                          ? 'border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed opacity-50'
                          : isSelected
                          ? `${config.bgColor} ${config.borderColor} ${config.color} shadow-sm`
                          : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <TypeIcon className="w-5 h-5" />
                      <span className="text-xs font-medium">{config.label}</span>
                      {isSelected && !isDisabled && (
                        <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full ${config.bgColor} border-2 border-white flex items-center justify-center shadow-sm`}>
                          <Check className={`w-3 h-3 ${config.color}`} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${currentTypeConfig.label.toLowerCase()}s...`}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* Items List */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              {isLoadingItems ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-brand-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Loading items...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center">
                  <div className={`w-12 h-12 ${currentTypeConfig.bgColor} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                    <CurrentTypeIcon className={`w-6 h-6 ${currentTypeConfig.color}`} />
                  </div>
                  <p className="text-sm font-medium text-gray-700">No items found</p>
                  <p className="text-xs text-gray-500 mt-1">Try adjusting your search</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 max-h-[280px] overflow-y-auto">
                  {items.map((item) => {
                    const isSelected = selectedItems.some(i => i.item_id === item._id);
                    return (
                      <div
                        key={item._id}
                        onClick={() => toggleItemSelection(item)}
                        className={`p-4 cursor-pointer transition-all flex items-center gap-4 ${
                          isSelected
                            ? `${currentTypeConfig.bgColor} bg-opacity-50`
                            : `hover:bg-gray-50`
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                          isSelected
                            ? `${currentTypeConfig.borderColor} ${currentTypeConfig.bgColor}`
                            : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className={`w-3 h-3 ${currentTypeConfig.color}`} />}
                        </div>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? currentTypeConfig.bgColor : 'bg-gray-100'
                        }`}>
                          <CurrentTypeIcon className={`w-5 h-5 ${isSelected ? currentTypeConfig.color : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                            {item.title}
                          </p>
                          {item.description && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.total_pages > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.total_pages}
                  totalItems={pagination.total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(perPage) => {
                    setItemsPerPage(perPage);
                    setCurrentPage(1);
                  }}
                  showItemsPerPage={true}
                  itemsPerPageOptions={[5, 10, 20]}
                  compact={true}
                />
              )}
            </div>

            {/* Selected Items Preview */}
            {selectedItems.length > 0 && (
              <div className={`rounded-xl p-4 border ${currentTypeConfig.bgColor} ${currentTypeConfig.borderColor} border-opacity-30`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${currentTypeConfig.color}`}>
                    Selected Items
                  </span>
                  <span className={`text-xs font-medium ${currentTypeConfig.color} ${currentTypeConfig.bgColor} px-2 py-1 rounded-full`}>
                    {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedItems.map((item) => (
                    <span
                      key={item.item_id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg text-sm shadow-sm border border-gray-100"
                    >
                      <CurrentTypeIcon className={`w-3.5 h-3.5 ${currentTypeConfig.color}`} />
                      <span className="text-gray-700 font-medium">{item.item_name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItems(prev => prev.filter(i => i.item_id !== item.item_id));
                        }}
                        className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Assignment Settings */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-4">
              <p className="text-sm font-semibold text-gray-700">Assignment Settings</p>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-brand-purple-300 transition-colors">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-brand-purple-600 focus:ring-brand-purple-500"
                    checked={settings.is_mandatory}
                    onChange={(e) => setSettings(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Mandatory</span>
                    <p className="text-xs text-gray-500">Required completion</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-brand-purple-300 transition-colors">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-brand-purple-600 focus:ring-brand-purple-500"
                    checked={settings.send_notification}
                    onChange={(e) => setSettings(prev => ({ ...prev, send_notification: e.target.checked }))}
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Notify Learner</span>
                    <p className="text-xs text-gray-500">Send email notification</p>
                  </div>
                </label>
              </div>

              {/* Deadline */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Deadline:</label>
                <div className="relative flex-1">
                  <input
                    type="number"
                    min="1"
                    placeholder="No deadline"
                    className={`w-full px-4 py-2.5 pr-16 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500 ${
                      deadlineError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                    }`}
                    value={settings.deadline_days ?? ''}
                    onChange={handleDeadlineDaysChange}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">days</span>
                </div>
              </div>
              {deadlineError && <p className="text-xs text-red-500">{deadlineError}</p>}

              {/* Auto Reminder */}
              {settings.send_notification && (
                <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-brand-purple-600 focus:ring-brand-purple-500"
                    checked={settings.enable_automatic_reminder}
                    onChange={(e) => setSettings(prev => ({ ...prev, enable_automatic_reminder: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">Enable automatic reminders</span>
                  {settings.enable_automatic_reminder && (
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-sm text-gray-500">every</span>
                      <input
                        type="number"
                        min="1"
                        className="w-16 px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
                        value={settings.reminder_frequency_days}
                        onChange={(e) => setSettings(prev => ({ ...prev, reminder_frequency_days: Math.max(1, parseInt(e.target.value) || 1) }))}
                      />
                      <span className="text-sm text-gray-500">days</span>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Message to Learner (Optional)</label>
                <textarea
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-purple-500 bg-white"
                  placeholder="Add a personalized message for the learner..."
                  value={settings.message}
                  onChange={(e) => setSettings(prev => ({ ...prev, message: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 disabled:opacity-50 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || !!deadlineError || selectedItems.length === 0}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 text-white rounded-xl hover:from-brand-purple-700 hover:to-brand-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-lg shadow-purple-500/25"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Suggest & Assign
          </button>
        </div>
      </div>
    </div>
  );
};

// Approve Confirmation Modal
const ApproveModal = ({
  isOpen,
  onClose,
  onConfirm,
  onCloseTicket,
  isLoading,
  ticket,
  selectedItemIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (settings: ApprovalSettings) => void;
  onCloseTicket: () => void;
  isLoading: boolean;
  ticket: SelfTicketRequestResponseDto;
  selectedItemIds: string[];
}): JSX.Element => {
  const typeConfig = typeConfigMap[ticket.item_type];
  const TypeIcon = typeConfig.icon;

  const [settings, setSettings] = useState<ApprovalSettings>({
    is_mandatory: false,
    deadline_days: undefined,
    send_notification: true,
    enable_automatic_reminder: false,
    reminder_frequency_days: 7,
    message: '',
    remarks: '',
  });

  const [deadlineError, setDeadlineError] = useState('');
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [duplicateItemIds, setDuplicateItemIds] = useState<Set<string>>(new Set());
  const [duplicateChecked, setDuplicateChecked] = useState(false);

  // Only consider items that are actually selected for approval
  const relevantItems = (ticket.items || []).filter(item => item && selectedItemIds?.includes(item.item_id));

  const newItems = relevantItems.filter(item => !duplicateItemIds.has(item.item_id));
  const duplicateItems = relevantItems.filter(item => duplicateItemIds.has(item.item_id));
  const allDuplicates = duplicateChecked && newItems.length === 0 && relevantItems.length > 0;
  // Pending items the admin did not select — if any remain, the ticket must not be denied
  // just because the selected ones are already assigned (e.g. course already assigned via a learning path)
  const unselectedPendingCount = (ticket.items || []).filter(item =>
    item && (!item.status || item.status === SelfTicketRequestStatus.PENDING) && !selectedItemIds?.includes(item.item_id)
  ).length;
  const canPickOtherItems = allDuplicates && unselectedPendingCount > 0;
  // Singular/plural label for a given count, e.g. labelFor(1) → "course", labelFor(2) → "courses"
  const labelFor = (count: number): string => pluralizeLabel(typeConfig.label, count).toLowerCase();
  const isOrAre = (count: number): string => (count === 1 ? 'is' : 'are');

  useEffect(() => {
    if (isOpen) {
      setSettings({
        is_mandatory: false,
        deadline_days: undefined,
        send_notification: true,
        enable_automatic_reminder: false,
        reminder_frequency_days: 7,
        message: '',
        remarks: '',
      });
      setDeadlineError('');
      setDuplicateItemIds(new Set());
      setDuplicateChecked(false);

      // Check duplicates for COURSE, CERTIFICATION, and LEARNING_PATH tickets on open
      const itemIds = relevantItems.map(item => item.item_id);
      if (itemIds.length > 0 && ticket.item_type !== SelfTicketItemType.GENERAL) {
        setIsCheckingDuplicates(true);

        let duplicateCheckPromise: Promise<Set<string>>;

        if (ticket.item_type === SelfTicketItemType.COURSE) {
          duplicateCheckPromise = courseAssignmentService.checkForDuplicates({
            type: 'users',
            targetIds: [ticket.requester_id],
            courseIds: itemIds,
          }).then(result => result.hasConflicts ? new Set(result.conflicts.map(c => c.courseId)) : new Set<string>());

        } else if (ticket.item_type === SelfTicketItemType.CERTIFICATION) {
          duplicateCheckPromise = checkCertDuplicates({
            type: 'users',
            target_ids: [ticket.requester_id],
            certifications_ids: itemIds,
          }).then(result => result.has_conflicts ? new Set(result.conflicts.map(c => c.certification_id)) : new Set<string>());

        } else if (ticket.item_type === SelfTicketItemType.LEARNING_PATH) {
          duplicateCheckPromise = learningPathAssignmentService.checkForDuplicates({
            type: 'users',
            targetIds: [ticket.requester_id],
            learningPathIds: itemIds,
          }).then(result => result.has_conflicts ? new Set(result.conflicts.map((c: any) => c.learning_path_id)) : new Set<string>());

        } else {
          duplicateCheckPromise = Promise.resolve(new Set<string>());
        }

        duplicateCheckPromise.then(conflictIds => {
          if (conflictIds.size > 0) {
            setDuplicateItemIds(conflictIds);
          }
          setDuplicateChecked(true);
        }).catch((err) => {
          console.warn('Duplicate check failed, proceeding without check:', err);
          setDuplicateChecked(true);
        }).finally(() => {
          setIsCheckingDuplicates(false);
        });
      } else {
        setDuplicateChecked(true);
      }
    }
  }, [isOpen]);

  const handleDeadlineDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setSettings(prev => ({ ...prev, deadline_days: undefined }));
      setDeadlineError('');
      return;
    }
    const days = parseInt(value);
    setDeadlineError(days <= 0 ? 'Deadline must be at least 1 day' : '');
    setSettings(prev => ({ ...prev, deadline_days: days }));
  };

  const handleConfirm = () => {
    if (deadlineError) return;
    // Pass only non-duplicate items so backend skips already-assigned ones
    if (duplicateChecked && duplicateItemIds.size > 0 && newItems.length > 0) {
      onConfirm({
        ...settings,
        items: newItems.map(item => ({ item_id: item.item_id, item_name: item.item_name })),
        skip_duplicate_items: true,
      });
    } else {
      onConfirm(settings);
    }
  };

  if (!isOpen) return <></>;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {canPickOtherItems
              ? `Selected ${pluralizeLabel(typeConfig.label, duplicateItems.length)} Already Assigned`
              : allDuplicates
                ? duplicateItems.length === 1
                  ? `${typeConfig.label} Already Assigned`
                  : `All ${pluralizeLabel(typeConfig.label, duplicateItems.length)} Already Assigned`
                : 'Approve Request'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XCircle className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content - scrolls inside the modal so header and footer stay visible on small screens */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {isCheckingDuplicates ? (
            <div className="flex items-center justify-center gap-3 py-8">
              <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-600">Checking for existing assignments...</p>
            </div>
          ) : (
            <>
              {/* Duplicate Warning */}
              {duplicateChecked && duplicateItems.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium text-amber-800">
                      {canPickOtherItems
                        ? `The selected ${labelFor(duplicateItems.length)} below ${isOrAre(duplicateItems.length)} already assigned to ${ticket.requester_name}`
                        : allDuplicates
                          ? duplicateItems.length === 1
                            ? `The requested ${labelFor(1)} is already assigned to ${ticket.requester_name}`
                            : `All requested ${labelFor(duplicateItems.length)} are already assigned to ${ticket.requester_name}`
                          : `${duplicateItems.length} of ${relevantItems.length} selected ${labelFor(relevantItems.length)} ${isOrAre(duplicateItems.length)} already assigned to ${ticket.requester_name} and will be skipped`}
                    </p>
                  </div>

                  {/* Single bordered list; fixed height with inner scroll so a long list doesn't stretch the modal */}
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                      <span className="text-xs font-semibold text-gray-600">Already assigned</span>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
                        {duplicateItems.length}
                      </span>
                    </div>
                    <ul className="max-h-[132px] overflow-y-auto divide-y divide-gray-100" style={{ scrollbarWidth: 'thin' }}>
                      {duplicateItems.map(item => (
                        <li key={item.item_id} className="flex items-center gap-2.5 px-3 py-2.5">
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${typeConfig.bgColor}`}>
                            <TypeIcon className={`w-3.5 h-3.5 ${typeConfig.color}`} />
                          </div>
                          {/* Native title: TooltipArrow is absolutely positioned and would be clipped by the scroll container */}
                          <p className="min-w-0 flex-1 text-sm text-gray-800 truncate cursor-default" title={item.item_name}>
                            {item.item_name}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {canPickOtherItems && (
                    <p className="text-xs text-gray-600">
                      {unselectedPendingCount} other {labelFor(unselectedPendingCount)} in this ticket {isOrAre(unselectedPendingCount)} still pending. Select {unselectedPendingCount === 1 ? 'it' : 'them'} from Requested Items to approve.
                    </p>
                  )}
                </div>
              )}

              {/* Info Banner - only if there are new items to assign */}
              {!allDuplicates && (
                <>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Approve & Assign to {ticket.requester_name}</p>
                      <p className="text-xs text-green-600 mt-0.5">{newItems.length} {labelFor(newItems.length)} will be assigned</p>
                    </div>
                  </div>

                  {/* Items List - Compact */}
                  <div className="flex flex-col gap-2">
                    {newItems.map((item) => (
                      <div key={item.item_id} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm min-w-0 ${typeConfig.bgColor} ${typeConfig.color}`}>
                        <TypeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <TooltipArrow content={item.item_name} position="top" delay={200} block>
                            <span className="block font-medium truncate cursor-default">{item.item_name}</span>
                          </TooltipArrow>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Assignment Settings - Compact Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={settings.is_mandatory}
                        onChange={(e) => setSettings(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                      />
                      <span className="text-sm text-gray-700">Mandatory</span>
                    </label>
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={settings.send_notification}
                        onChange={(e) => setSettings(prev => ({ ...prev, send_notification: e.target.checked }))}
                      />
                      <span className="text-sm text-gray-700">Send Notification</span>
                    </label>
                  </div>

                  {/* Deadline */}
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-700 whitespace-nowrap">Deadline:</label>
                    <div className="relative flex-1">
                      <input
                        type="number"
                        min="1"
                        placeholder="No deadline"
                        className={`w-full px-3 py-2 pr-14 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          deadlineError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={settings.deadline_days ?? ''}
                        onChange={handleDeadlineDaysChange}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">days</span>
                    </div>
                    {settings.deadline_days && settings.deadline_days > 0 && !deadlineError && (
                      <span className="text-xs text-green-600 font-medium whitespace-nowrap">
                        Due: {new Date(Date.now() + settings.deadline_days * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  {deadlineError && <p className="text-xs text-red-500 -mt-2">{deadlineError}</p>}

                  {/* Auto Reminder */}
                  {settings.send_notification && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={settings.enable_automatic_reminder}
                        onChange={(e) => setSettings(prev => ({ ...prev, enable_automatic_reminder: e.target.checked }))}
                      />
                      <span className="text-sm text-gray-700">Enable automatic reminders</span>
                      {settings.enable_automatic_reminder && (
                        <>
                          <span className="text-sm text-gray-500">every</span>
                          <input
                            type="number"
                            min="1"
                            className="w-14 px-2 py-1 text-sm border rounded text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                            value={settings.reminder_frequency_days}
                            onChange={(e) => setSettings(prev => ({ ...prev, reminder_frequency_days: Math.max(1, parseInt(e.target.value) || 1) }))}
                          />
                          <span className="text-sm text-gray-500">days</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label className="text-sm text-gray-700">Message (Optional)</label>
                    <textarea
                      rows={2}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Add a message for the learner..."
                      value={settings.message}
                      onChange={(e) => setSettings(prev => ({ ...prev, message: e.target.value }))}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!isCheckingDuplicates && (
          <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-2xl flex-shrink-0">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 font-medium"
            >
              Cancel
            </button>
            {canPickOtherItems ? (
              <button
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
              >
                Select Other {pluralizeLabel(typeConfig.label, unselectedPendingCount)}
              </button>
            ) : allDuplicates ? (
              <button
                onClick={onCloseTicket}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Deny Request
              </button>
            ) : (
              <button
                onClick={handleConfirm}
                disabled={isLoading || !!deadlineError}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve{duplicateItems.length > 0 ? ` (${newItems.length} item${newItems.length > 1 ? 's' : ''})` : ''}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
export default function AdminTicketDetail(): JSX.Element {
  // Permission guard - requires WRITE permission to access ticket details
  usePermissionRouteAccess([TICKET_REQUESTS_PERMISSION.ADMIN_TICKET_REQUESTS_WRITE]);

  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDenying, setIsDenying] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showClearChatModal, setShowClearChatModal] = useState(false);
  const [showApproversList, setShowApproversList] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showNotApproverWarning, setShowNotApproverWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'deny' | 'suggest' | null>(null);
  const [showRemoveApproverModal, setShowRemoveApproverModal] = useState<any>(false);
  const [selectedApprover, setSelectedApprover] = useState<any>(null);
  const [isRemovingApprover, setIsRemovingApprover] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch ticket details with polling every 5 seconds for real-time messages
  // Also mark as read on every fetch so sender sees blue tick
  const {
    data: ticket,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['ticket-detail', ticketId],
    queryFn: async () => {
      // First mark as read to update is_read_by on messages
      await ticketService.markAsRead(ticketId!).catch(() => { });
      // Then fetch the updated ticket details
      return ticketService.getRequestByIdForAdmin(ticketId!);
    },
    enabled: !!ticketId,
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  // Start with nothing selected; admin must pick items before Approve is enabled. Keyed on
  // id + status (not the ticket object) because the query polls every 5s and would wipe the selection.
  useEffect(() => {
    setSelectedItemIds([]);
  }, [ticket?._id, ticket?.status]);

  // Get current user ID from auth context
  const { user } = useAuth();
  const currentUserId = user?.id || '';

  // Check if current user is an approver
  const isCurrentUserApprover = ticket?.approvers?.some(a => a.user_id === currentUserId) || false;

  // Send message mutation - uses setQueryData for smooth updates without blinking
  const sendMessageMutation = useMutation({
    mutationFn: (message: string) => ticketService.addMessage(ticketId!, { message }),
    onMutate: async (newMessage: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['ticket-detail', ticketId] });

      // Snapshot the previous value
      const previousTicket = queryClient.getQueryData<SelfTicketRequestResponseDto>(['ticket-detail', ticketId]);

      // Optimistically update the cache with new message
      if (previousTicket) {
        const optimisticMessage: TicketMessageDto = {
          _id: `temp-${Date.now()}`,
          sender_id: currentUserId || 'admin-1',
          sender_name: 'You',
          sender_role: 'ADMIN',
          message: newMessage,
          sent_at: new Date().toISOString(),
          is_read_by: [currentUserId || 'admin-1'],
          reactions: [],
          is_deleted: false,
        };

        queryClient.setQueryData<SelfTicketRequestResponseDto>(['ticket-detail', ticketId], {
          ...previousTicket,
          messages: [...(previousTicket.messages || []), optimisticMessage],
        });
      }

      // Return context with previous value for rollback
      return { previousTicket };
    },
    onError: (_error, _newMessage, context) => {
      // Rollback to previous value on error
      if (context?.previousTicket) {
        queryClient.setQueryData(['ticket-detail', ticketId], context.previousTicket);
      }
      toast.error('Failed to send message. Please try again.');
    },
    onSettled: () => {
      // Sync with server after mutation (success or error)
      // Use a slight delay to allow polling to pick up the new message naturally
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
      }, 1000);
    },
  });

  // Add reaction mutation
  const addReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      ticketService.addReaction(ticketId!, messageId, { emoji }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
    },
    onError: () => {
      toast.error('Failed to add reaction.');
    },
  });

  // Remove reaction mutation
  const removeReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      ticketService.removeReaction(ticketId!, messageId, { emoji }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
    },
    onError: () => {
      toast.error('Failed to remove reaction.');
    },
  });

  // Delete message mutation
  const deleteMessageMutation = useMutation({
    mutationFn: ({ messageId, deleteType }: { messageId: string; deleteType: 'for_everyone' | 'for_me' }) =>
      ticketService.deleteMessage(ticketId!, messageId, { delete_type: deleteType }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
      toast.success(variables.deleteType === 'for_everyone' ? 'Message deleted for everyone.' : 'Message deleted.');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.error_description || 'Failed to delete message.';
      toast.error(message);
    },
  });

  // Clear chat mutation
  const clearChatMutation = useMutation({
    mutationFn: () => ticketService.clearChat(ticketId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
      setShowClearChatModal(false);
      toast.success('Chat cleared.');
    },
    onError: () => {
      toast.error('Failed to clear chat.');
    },
  });

  // Close ticket mutation
  const closeTicketMutation = useMutation({
    mutationFn: () => ticketService.closeTicket(ticketId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['received-ticket-requests'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-request-stats'] });
      setIsClosing(false);
      setShowCloseModal(false);
      toast.success('Ticket has been closed.');
      navigate('/admin/settings/ticket-requests');
    },
    onError: () => {
      setIsClosing(false);
      toast.error('Failed to close ticket.');
    },
  });

  // Remove approver handler
  const handleRemoveApprover = async (userId: string) => {
    setIsRemovingApprover(true);
    try {
      await ticketService.removeApprover(ticketId!, userId);
      queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['received-ticket-requests'] });
      setShowRemoveApproverModal(false);
      setSelectedApprover(null);
      toast.success('Approver removed successfully.');
    } catch (error: any) {
      const msg = error?.response?.data?.error?.error_description || 'Failed to remove approver.';
      toast.error(msg);
    } finally {
      setIsRemovingApprover(false);
    }
  };

  // Form setup
  const { register, handleSubmit, reset, watch, setValue, getValues } = useForm<MessageFormData>({
    defaultValues: { message: '' },
  });

  const messageValue = watch('message');

  // Map API messages to UI format (optimistic updates are handled in the cache)
  const allMessages: ChatMessage[] = (ticket?.messages || []).map((msg) => mapApiMessageToChat(msg, currentUserId));

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  // Close emoji picker and menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group messages by date
  const groupedMessages = allMessages.reduce((groups, message) => {
    const date = formatMessageDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, ChatMessage[]>);

  // Handle emoji selection for input
  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const currentMessage = getValues('message');
    setValue('message', currentMessage + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Send message handler
  const onSubmit = (data: MessageFormData): void => {
    if (!data.message.trim() || sendMessageMutation.isPending) return;

    const messageText = data.message.trim();
    reset();

    // Send message via API - optimistic update is handled in onMutate
    sendMessageMutation.mutate(messageText);
  };

  // Handle reaction
  const handleReaction = (messageId: string, emoji: string) => {
    addReactionMutation.mutate({ messageId, emoji });
  };

  // Handle remove reaction
  const handleRemoveReaction = (messageId: string, emoji: string) => {
    removeReactionMutation.mutate({ messageId, emoji });
  };

  // Handle delete message
  const handleDeleteMessage = (messageId: string, deleteType: 'for_everyone' | 'for_me') => {
    deleteMessageMutation.mutate({ messageId, deleteType });
  };

  // Handle clear chat
  const handleClearChat = () => {
    clearChatMutation.mutate();
  };

  // Handle close ticket confirmation
  const handleCloseTicket = () => {
    setIsClosing(true);
    closeTicketMutation.mutate();
  };

  // Open close confirmation modal
  const openCloseModal = () => {
    setShowCloseModal(true);
  };

  // Approve handler (also handles suggest for GENERAL tickets)
  const handleApprove = (settings: ApprovalSettings): void => {
    if (!ticketId) return;
    setIsApproving(true);

    const isSuggest = settings.item_type && settings.items && settings.items.length > 0 && !settings.skip_duplicate_items;
    const payload: Omit<ApprovalSettings, 'skip_duplicate_items'> = {
      remarks: settings.remarks || undefined,
      is_mandatory: settings.is_mandatory,
      deadline_days: settings.deadline_days || undefined,
      send_notification: settings.send_notification,
      enable_automatic_reminder: settings.send_notification ? settings.enable_automatic_reminder : false,
      reminder_frequency_days: settings.send_notification && settings.enable_automatic_reminder ? settings.reminder_frequency_days : undefined,
      message: settings.message || undefined,
      // For GENERAL tickets (suggest functionality) or when duplicates were filtered
      item_type: settings.item_type,
      items: settings.items,
      selected_item_ids: selectedItemIds.length > 0 ? selectedItemIds : undefined,
    };

    ticketService.approveRequest(ticketId, payload)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
        queryClient.invalidateQueries({ queryKey: ['received-ticket-requests'] });
        queryClient.invalidateQueries({ queryKey: ['ticket-request-stats'] });
        setShowApproveModal(false);
        setShowSuggestModal(false);
        toast.success(isSuggest
          ? 'Items suggested and assigned to the learner successfully!'
          : 'Request approved successfully! Items will be assigned to the learner.');
        navigate('/admin/settings/ticket-requests');
      })
      .catch(() => {
        toast.error(isSuggest
          ? 'Failed to suggest items. Please try again.'
          : 'Failed to approve request. Please try again.');
      })
      .finally(() => {
        setIsApproving(false);
      });
  };

  // Deny handler
  const handleDeny = (reason: string): void => {
    if (!ticketId) return;
    setIsDenying(true);
    // Deny rejects the whole ticket (all pending items) and closes it
    ticketService.denyRequest(ticketId, { denial_reason: reason })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['ticket-detail', ticketId] });
        queryClient.invalidateQueries({ queryKey: ['received-ticket-requests'] });
        queryClient.invalidateQueries({ queryKey: ['ticket-request-stats'] });
        setShowDenyModal(false);
        toast.success('Request has been denied. The learner will be notified.');
        navigate('/admin/settings/ticket-requests');
      })
      .catch(() => {
        toast.error('Failed to deny request. Please try again.');
      })
      .finally(() => {
        setIsDenying(false);
      });
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (isError || !ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <XCircle className="w-16 h-16 mb-4 text-red-400" />
        <p className="font-semibold text-lg">Failed to load ticket details</p>
        <p className="text-sm mt-1">Please try again later</p>
        <button
          onClick={() => navigate('/admin/settings/ticket-requests')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="-mx-4 sm:-mx-6 -mt-6 -mb-6 h-[calc(100vh-64px)] flex flex-col bg-gray-50 overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-4 sm:px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/admin/settings/ticket-requests')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${typeConfigMap[ticket.item_type].bgColor}`}>
                  {(() => {
                    const TypeIcon = typeConfigMap[ticket.item_type].icon;
                    return <TypeIcon className={`w-5 h-5 ${typeConfigMap[ticket.item_type].color}`} />;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono font-semibold text-primary-600">{ticket.ticket_number}</span>
                    <span className="text-gray-300">|</span>
                    <span className={`font-medium ${typeConfigMap[ticket.item_type].color}`}>{typeConfigMap[ticket.item_type].label} Ticket</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-700 font-medium">{ticket.requester_name}</span>
                  </div>
                  {/* Show request_message below ticket info for all ticket types */}
                  {ticket.request_message && (
                    <p className="text-xs text-gray-500 mt-1 break-words">
                      {ticket.request_message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MoreVertical className="w-5 h-5 text-gray-500" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-20">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowApproversList(true);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Approvers List
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowClearChatModal(true);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear Chat
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clear Chat Confirmation Modal */}
        {showClearChatModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear Chat</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to clear this chat? Messages will only be hidden for you, not deleted from the server.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearChatModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearChat}
                  disabled={clearChatMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {clearChatMutation.isPending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Chat Section */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 custom-scrollbar">
              {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                <div key={date}>
                  {/* Date Separator */}
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-gray-200 text-gray-600 text-xs font-medium px-3 py-1 rounded-full">
                      {date}
                    </div>
                  </div>

                  {/* Messages for this date */}
                  <div className="space-y-3">
                    {dateMessages.map((message, index) => {
                      // System messages are displayed differently
                      if (message.sender_role === 'system') {
                        return (
                          <div key={message.id} className="flex justify-center my-2">
                            <div className="bg-gray-100 text-gray-600 text-xs px-4 py-1.5 rounded-full">
                              {message.message}
                            </div>
                          </div>
                        );
                      }

                      const isOwn = message.sender_id === currentUserId;
                      const prevMessage = dateMessages[index - 1];
                      const showAvatar = !prevMessage || prevMessage.sender_id !== message.sender_id || prevMessage.sender_role === 'system';

                      // Get sender's profile image
                      const senderProfileImage = message.sender_role === 'requester'
                        ? ticket.requester_profile_image
                        : ticket.approvers?.find(a => a.user_id === message.sender_id)?.profile_image;

                      return (
                        <MessageBubble
                          key={message.id}
                          message={message}
                          isOwn={isOwn}
                          showAvatar={showAvatar}
                          currentUserId={currentUserId}
                          onReact={handleReaction}
                          onRemoveReaction={handleRemoveReaction}
                          onDelete={handleDeleteMessage}
                          ticketStatus={ticket.status}
                          approverIds={ticket.approvers?.map(a => a.user_id) || []}
                          requesterId={ticket.requester_id}
                          senderProfileImage={senderProfileImage}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="bg-white border-t p-4 flex-shrink-0">
              {ticket.status !== SelfTicketRequestStatus.CLOSED ? (
                <>
                <form onSubmit={handleSubmit(onSubmit)} className="flex items-end gap-2">
                  {/* Emoji picker button */}
                  <div className="relative" ref={emojiPickerRef}>
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0 mb-2"
                    >
                      <Smile className="w-5 h-5" />
                    </button>
                    {showEmojiPicker && (
                      <div className="absolute bottom-full left-0 mb-2 z-20">
                        <EmojiPicker
                          onEmojiClick={handleEmojiSelect}
                          width={320}
                          height={400}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 relative">
                    <textarea
                      {...register('message')}
                      placeholder="Type your message..."
                      rows={1}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm max-h-32"
                      onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          const msg = getValues('message')?.trim();
                          if (msg && !sendMessageMutation.isPending) {
                            reset();
                            sendMessageMutation.mutate(msg);
                          }
                        }
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!messageValue?.trim() || sendMessageMutation.isPending}
                    className="p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 mb-2"
                  >
                    <Send className={`w-5 h-5 ${sendMessageMutation.isPending ? 'animate-pulse' : ''}`} />
                  </button>
                </form>
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 rounded-xl">
                  <Lock className="w-5 h-5 text-gray-600" />
                  <p className="text-sm text-gray-600">This ticket has been <span className="font-medium text-gray-700">closed</span>. Chat is no longer available.</p>
                </div>
              )}
            </div>
          </div>

          {/* Ticket Details Sidebar - Hidden on mobile */}
          <div className="hidden lg:flex lg:flex-col w-80 xl:w-[380px] border-l bg-white flex-shrink-0">
            <TicketDetailsSidebar
              ticket={ticket}
              onApprove={() => {
                const isGeneral = ticket.item_type === SelfTicketItemType.GENERAL;
                if (!isCurrentUserApprover) {
                  setPendingAction(isGeneral ? 'suggest' : 'approve');
                  setShowNotApproverWarning(true);
                } else {
                  if (isGeneral) {
                    setShowSuggestModal(true);
                  } else {
                    setShowApproveModal(true);
                  }
                }
              }}
              onDeny={() => {
                if (!isCurrentUserApprover) {
                  setPendingAction('deny');
                  setShowNotApproverWarning(true);
                } else {
                  setShowDenyModal(true);
                }
              }}
              onClose={openCloseModal}
              isApproving={isApproving}
              isDenying={isDenying}
              isClosing={isClosing}
              showApproversList={showApproversList}
              setShowApproversList={setShowApproversList}
              isGeneral={ticket.item_type === SelfTicketItemType.GENERAL}
              onRemoveApproverClick={(approver) => {
                setSelectedApprover(approver);
                setShowRemoveApproverModal(true);
              }}
              selectedItemIds={selectedItemIds}
              setSelectedItemIds={setSelectedItemIds}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <ApproveModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApprove}
        onCloseTicket={() => {
          setShowApproveModal(false);
          const pendingCount = (ticket.items || []).filter(item =>
            item && (!item.status || item.status === SelfTicketRequestStatus.PENDING)
          ).length;
          const typeLabel = pluralizeLabel(typeConfigMap[ticket.item_type]?.label || 'Item', pendingCount).toLowerCase();
          handleDeny(pendingCount === 1
            ? `The requested ${typeLabel} is already assigned to this user.`
            : `All requested ${typeLabel} are already assigned to this user.`);
        }}
        isLoading={isApproving}
        ticket={ticket}
        selectedItemIds={selectedItemIds}
      />
      <DenyModal
        isOpen={showDenyModal}
        onClose={() => setShowDenyModal(false)}
        onConfirm={handleDeny}
        isLoading={isDenying}
      />
      <SuggestModal
        isOpen={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        onConfirm={handleApprove}
        isLoading={isApproving}
        ticket={ticket}
      />

      {/* Close Ticket Confirmation Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Close Ticket</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to close this ticket? This action will mark the ticket as closed and it cannot be reopened.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCloseModal(false)}
                disabled={isClosing}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseTicket}
                disabled={isClosing}
                className="flex-1 px-4 py-2.5 bg-gray-600 text-white rounded-xl hover:bg-gray-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {isClosing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                Close Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Not Approver Warning Modal */}
      {showNotApproverWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">You Are Not An Approver</h3>
                <p className="text-sm text-gray-500">Action required before proceeding</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <UserPlus className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium mb-1">
                    You are not currently assigned as an approver for this ticket.
                  </p>
                  <p className="text-sm text-amber-700">
                    If you continue, you will be automatically added as an approver to this ticket.
                    You will also receive email notifications related to this ticket.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowNotApproverWarning(false);
                  setPendingAction(null);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowNotApproverWarning(false);
                  if (pendingAction === 'approve') {
                    setShowApproveModal(true);
                  } else if (pendingAction === 'deny') {
                    setShowDenyModal(true);
                  } else if (pendingAction === 'suggest') {
                    setShowSuggestModal(true);
                  }
                  setPendingAction(null);
                }}
                className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-medium flex flex-col items-center justify-center transition-colors"
              >
                <span className="text-xs">Continue</span>
                <span className="text-xs">& Add Me as Approver</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Remove Approver Confirmation Modal */}
      {selectedApprover && (
        <DeleteConfirmationModal
          isOpen={showRemoveApproverModal}
          onClose={() => {
            setShowRemoveApproverModal(false);
            setSelectedApprover(null);
          }}
          onConfirm={() => handleRemoveApprover(selectedApprover.user_id)}
          title="Remove Approver"
          message="Are you sure you want to remove this approver from the request?"
          itemName={`${selectedApprover.user_name} (${selectedApprover.email})`}
          isLoading={isRemovingApprover}
          confirmButtonText="Remove"
          variant="danger"
        />
      )}
    </>
  );
}
